import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  Button, 
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  Fade,
  Grow,
  Zoom
} from '@mui/material';
import { 
  AccountBalance, 
  AttachMoney, 
  CreditCard, 
  Payments,
  Phone,
  Email,
  LocationOn,
  CheckCircle,
  MobileFriendly,
  Security,
  SupportAgent,
  ArrowForward
} from '@mui/icons-material';

// Import images
import personalHero from '../images/z.jpg';
import silverAccount from '../images/silver.jpg';
import goldAccount from '../images/gold.jpg';
import platinumAccount from '../images/platinum.jpg';
import diamondAccount from '../images/diamond.webp';

// Define color palette
const colors = {
  primary: '#132714', // Dark green
  secondary: '#57a649', // Vibrant green
  accent: '#f5a623', // Gold accent
  lightBg: '#f8fffb', // Very light green
  textDark: '#132714',
  textLight: '#ffffff'
};

const Personal = () => {
  const accountTypes = [
    {
      icon: <AttachMoney sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Silver Account",
      description: "Perfect for everyday banking with essential features",
      features: [
        "Low E10 activation fee",
        "6 free ATM withdrawals",
        "Basic online banking",
        "E20 monthly maintenance"
      ],
      action: "View Details",
      image: silverAccount,
      color: 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)' // Silver gradient
    },
    {
      icon: <CreditCard sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Gold Account",
      description: "Enhanced features for more frequent banking needs",
      features: [
        "E70 monthly fee",
        "8 free ATM withdrawals",
        "30 free POS transactions",
        "Priority customer service"
      ],
      action: "View Details",
      image: goldAccount,
      color: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)' // Gold gradient
    },
    {
      icon: <Payments sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Platinum Account",
      description: "Premium services for high-value banking",
      features: [
        "E125 monthly fee",
        "12 free ATM withdrawals",
        "Unlimited POS transactions",
        "Dedicated relationship manager"
      ],
      action: "View Details",
      image: platinumAccount,
      color: 'linear-gradient(135deg, #E0E0E0 0%, #B8B8B8 100%)' // Platinum gradient
    },
    {
      icon: <AccountBalance sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Diamond Account",
      description: "Exclusive benefits for our most valued customers",
      features: [
        "E300 monthly fee",
        "Unlimited ATM withdrawals",
        "Global concierge services",
        "Premium travel benefits"
      ],
      action: "View Details",
      image: diamondAccount,
      color: 'linear-gradient(135deg, #b9f2ff 0%, #7ec4cf 100%)' // Diamond gradient
    }
  ];

  const features = [
    {
      icon: <MobileFriendly sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Mobile Banking",
      description: "Bank anytime, anywhere with our secure app"
    },
    {
      icon: <Security sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "Advanced Security",
      description: "Multi-layered protection for your accounts"
    },
    {
      icon: <SupportAgent sx={{ fontSize: 40, color: colors.secondary }} />,
      title: "24/7 Support",
      description: "Dedicated customer service anytime"
    }
  ];

  return (
    <Box sx={{ overflowX: 'hidden', backgroundColor: colors.lightBg }}>
      {/* Hero Section */}
      <Box
        sx={{
          height: '80vh',
          minHeight: '600px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `linear-gradient(to right, rgba(19, 39, 20, 0.9), rgba(87, 166, 73, 0.7)), url(${personalHero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '100px',
            background: `linear-gradient(to top, ${colors.lightBg}, transparent)`,
            zIndex: 1
          }
        }}
      >
        <Container>
          <Fade in timeout={800}>
            <Box>
              <Typography 
                variant="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                  mb: 3,
                  lineHeight: 1.2
                }}
              >
                Personal <Box component="span" sx={{ color: colors.accent }}>Banking</Box> Solutions
              </Typography>
              <Typography 
                variant="h5"
                sx={{ 
                  maxWidth: '800px',
                  mx: 'auto',
                  mb: 4,
                  textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  fontSize: { xs: '1.1rem', md: '1.5rem' },
                  fontWeight: 300,
                  opacity: 0.9
                }}
              >
                Accounts designed to fit your lifestyle and financial needs
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  color="secondary" 
                  size="large"
                  href="/apply"
                  sx={{ 
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    boxShadow: `0 4px 20px ${colors.secondary}80`,
                    '&:hover': {
                      boxShadow: `0 6px 25px ${colors.secondary}b3`,
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease',
                    backgroundColor: colors.secondary,
                    fontWeight: 600
                  }}
                >
                  Open an Account
                </Button>
                <Button 
                  variant="outlined" 
                  size="large"
                  href="/compare"
                  sx={{ 
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    borderWidth: '2px',
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderWidth: '2px'
                    },
                    transition: 'all 0.3s ease',
                    fontWeight: 600
                  }}
                >
                  Compare Accounts
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      <Container sx={{ py: 10 }}>
        {/* Current Account Section */}
        <Grid container spacing={6} sx={{ mb: 10 }}>
          <Grid item xs={12} md={6}>
            <Fade in timeout={1000}>
              <Box>
                <Typography 
                  variant="h2" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 700,
                    color: colors.primary,
                    mb: 3,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -10,
                      left: 0,
                      width: '80px',
                      height: '4px',
                      backgroundColor: colors.secondary,
                      borderRadius: '2px'
                    }
                  }}
                >
                  Personal Current Account
                </Typography>
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  Your everyday banking companion
                </Typography>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
                  Manage your finances effortlessly with our feature-packed current account. Pay bills, send money, 
                  and track your spending with our intuitive digital banking platforms.
                </Typography>
                <Box sx={{ mb: 4 }}>
                  {[
                    "No minimum balance requirement",
                    "Free online and mobile banking",
                    "Instant money transfers",
                    "Customizable alerts and notifications"
                  ].map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <CheckCircle sx={{ color: colors.secondary, mr: 2 }} />
                      <Typography variant="body1" sx={{ color: colors.textDark }}>{item}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    href="/apply"
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      borderRadius: '8px',
                      boxShadow: `0 4px 12px ${colors.secondary}80`,
                      '&:hover': {
                        boxShadow: `0 6px 16px ${colors.secondary}b3`,
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease',
                      backgroundColor: colors.secondary,
                      fontWeight: 600
                    }}
                  >
                    Open Your Account
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    href="/features"
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      borderRadius: '8px',
                      borderWidth: '2px',
                      borderColor: colors.secondary,
                      color: colors.secondary,
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: `${colors.secondary}08`,
                        borderWidth: '2px'
                      }
                    }}
                  >
                    View Features
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grow in timeout={1000}>
              <Paper
                elevation={6}
                sx={{
                  borderRadius: '20px',
                  overflow: 'hidden',
                  height: '100%',
                  minHeight: 400,
                  position: 'relative',
                  transform: 'perspective(1000px) rotateY(-5deg)',
                  '&:hover': {
                    transform: 'perspective(1000px) rotateY(-5deg) translateY(-10px)',
                    boxShadow: '0 30px 60px -10px rgba(0,0,0,0.3)'
                  },
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(to bottom right, ${colors.secondary}20, ${colors.primary}30)`,
                    zIndex: 1
                  }
                }}
              >
                <Box
                  component="img"
                  src={personalHero}
                  alt="Personal Banking"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: 3,
                  background: `linear-gradient(to top, ${colors.primary}, transparent)`,
                  color: 'white',
                  zIndex: 2,
                  textAlign: 'left'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Digital Banking</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Experience our next-gen banking platform</Typography>
                </Box>
              </Paper>
            </Grow>
          </Grid>
        </Grid>

        {/* Account Types Section */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: colors.secondary,
                fontWeight: 600,
                letterSpacing: 1,
                mb: 1
              }}
            >
              CHOOSE YOUR ACCOUNT
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700,
                color: colors.primary,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100px',
                  height: '4px',
                  background: colors.secondary,
                  borderRadius: '2px'
                }
              }}
            >
              Account Options
            </Typography>
            <Typography variant="h5" sx={{ mt: 3, color: 'text.secondary', fontWeight: 300 }}>
              Find the perfect account for your financial needs
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            {accountTypes.map((account, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Zoom in timeout={800} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Card
                    elevation={3}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      background: 'white',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                      border: `1px solid ${colors.secondary}20`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                        borderColor: `${colors.secondary}40`,
                        transform: 'translateY(-10px)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        height: 150,
                        background: account.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          width: '100%',
                          height: '20px',
                          background: 'linear-gradient(to bottom, transparent, white)'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255,255,255,0.3)',
                          backdropFilter: 'blur(5px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                        }}
                      >
                        {account.icon}
                      </Box>
                    </Box>
                    <Box sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h5" gutterBottom sx={{ color: colors.primary, fontWeight: 600, textAlign: 'center' }}>
                        {account.title}
                      </Typography>
                      <Typography variant="body1" paragraph sx={{ color: 'text.secondary', textAlign: 'center', mb: 2 }}>
                        {account.description}
                      </Typography>
                      <List dense sx={{ mb: 2, flexGrow: 1 }}>
                        {account.features.map((feature, i) => (
                          <ListItem key={i} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: colors.secondary
                                }}
                              />
                            </ListItemIcon>
                            <Typography variant="body2" sx={{ color: colors.textDark }}>{feature}</Typography>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                    <Box sx={{ p: 3 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        href={`/accounts/${account.title.toLowerCase().replace(/\s+/g, '-')}`}
                        sx={{
                          borderRadius: '50px',
                          py: 1.5,
                          backgroundColor: colors.secondary,
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: '#4a8f3d'
                          }
                        }}
                      >
                        {account.action}
                      </Button>
                    </Box>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Features Section */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: colors.secondary,
                fontWeight: 600,
                letterSpacing: 1,
                mb: 1
              }}
            >
              WHY CHOOSE US
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700,
                color: colors.primary,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100px',
                  height: '4px',
                  background: colors.secondary,
                  borderRadius: '2px'
                }
              }}
            >
              Banking Benefits
            </Typography>
            <Typography variant="h5" sx={{ mt: 3, color: 'text.secondary', fontWeight: 300 }}>
              Experience banking that works for you
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <Grow in timeout={800} style={{ transitionDelay: `${index * 150}ms` }}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      borderRadius: '16px',
                      background: 'white',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                      border: `1px solid ${colors.secondary}20`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                        borderColor: `${colors.secondary}40`,
                        transform: 'translateY(-5px)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: `${colors.secondary}10`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ color: colors.primary, fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      {feature.description}
                    </Typography>
                  </Paper>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            backgroundColor: colors.primary,
            color: 'white',
            borderRadius: '16px',
            p: 8,
            mb: 6,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: 'radial-gradient(rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
              opacity: 0.3
            }
          }}
        >
          <Fade in timeout={800}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h2" gutterBottom sx={{ fontWeight: 700 }}>
                Ready to Get Started?
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, fontWeight: 300 }}>
                Open your account today in just 10 minutes
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  href="/apply"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    boxShadow: `0 4px 20px ${colors.accent}80`,
                    backgroundColor: colors.accent,
                    color: colors.primary,
                    fontWeight: 600,
                    '&:hover': {
                      boxShadow: `0 6px 25px ${colors.accent}b3`,
                      backgroundColor: '#f5b840',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Apply Now
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="/contact"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '50px',
                    borderWidth: '2px',
                    borderColor: 'white',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderWidth: '2px'
                    }
                  }}
                >
                  Contact Us
                </Button>
              </Box>
            </Box>
          </Fade>
        </Box>
      </Container>
    </Box>
  );
};

export default Personal;